import { Button, Descriptions, Form, Grid } from 'antd';
import dayjs from 'dayjs';
import { useReducer } from 'react';
import { APOLLO_DATE_FORMAT, DATE_FORMAT, FileUploadSpec } from '../../../../../../../../constants';
import {
  IdentityVerificationCaseV1,
  IdentityVerificationCaseV1CaseDetails,
  IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails,
  IdentityVerificationCaseV1RevisionRequest,
  IdentityVerificationCaseV1RevisionRequestRequestedChanges
} from '../../../../../../../../generated';
import { Country } from '../../../../../../../../types/country';
import { IdCaseFileUploadButtonWithPreviewContainer } from '../../../../../../../shared/file-upload-button-with-preview/id-case-file-upload-button-with-preview.container';
import { AzureBlobResponse } from '../../../../../../../shared/file-upload/azure-upload';
import { CaseDetailsApplicationPassportDetailForm } from './case-details-application-passport-detail-form';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

interface CaseDetailsApplicationProps {
  data: IdentityVerificationCaseV1;
  caseId: string;
  onSave: (value: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails) => Promise<void>;
  onSaveAndComplete: (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails, revisionRequest: IdentityVerificationCaseV1RevisionRequest) => Promise<void>;
  countryData: Country[];
  onUploadSuccess: (uploadedData: AzureBlobResponse, fieldName: string) => Promise<void>;
  isSavingApplication: boolean;
}

interface ApplicationFormState {
  isEditingPassportDetails: boolean;
  isEditingPhotoUpload: boolean;
  isEditingPassportUploads: boolean;
  requestedUpdate: IdentityVerificationCaseV1RevisionRequestRequestedChanges;
}
const formatDate = (date: string) => {
  return dayjs(date).utc().format(DATE_FORMAT).toUpperCase();
};

export const CaseDetailsApplication: React.FC<CaseDetailsApplicationProps> = (props) => {
  const [passportDetailsForm] = Form.useForm<IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails>();
  const { caseDetails: initialCaseDetails, revisionRequest: initialRevisionRequest } = props.data || {};
  const initialState: ApplicationFormState = {
    isEditingPassportDetails: false,
    isEditingPhotoUpload: false,
    isEditingPassportUploads: false,

    requestedUpdate: {
      requestUpdatedPassportDetails: initialRevisionRequest?.requestedChanges?.requestUpdatedPassportDetails,
      requestUploadExpirationPage: initialRevisionRequest?.requestedChanges?.requestUploadExpirationPage,
      requestUploadPassport: initialRevisionRequest?.requestedChanges?.requestUploadPassport,
      requestUploadPassportTranslation: initialRevisionRequest?.requestedChanges?.requestUploadPassportTranslation,
      requestUploadPhoto: initialRevisionRequest?.requestedChanges?.requestUploadPhoto
    }
  };

  const reducer = (currentState: ApplicationFormState, action: { type: string }): ApplicationFormState => {
    switch (action.type) {
      case 'edit-passport-details':
        return {
          ...currentState,
          isEditingPassportDetails: true,
          requestedUpdate: {
            ...currentState.requestedUpdate,
            requestUpdatedPassportDetails: false
          }
        };
      case 'edit-passport-uploads':
        return {
          ...currentState,
          isEditingPassportUploads: true
        };
      case 'edit-photo':
        return {
          ...currentState,
          isEditingPhotoUpload: true
        } as ApplicationFormState;
      case 'cancel':
        return {
          ...currentState,
          isEditingPassportDetails: false,
          isEditingPhotoUpload: false,
          isEditingPassportUploads: false,
          requestedUpdate: { ...initialRevisionRequest?.requestedChanges }
        };
      case 'save':
        return { ...currentState, isEditingPassportDetails: false, isEditingPhotoUpload: false, isEditingPassportUploads: false };
      case 'save-complete':
        return {
          ...currentState,
          isEditingPassportDetails: false,
          isEditingPhotoUpload: false,
          isEditingPassportUploads: false,
          requestedUpdate: {
            requestUpdatedPassportDetails: false,
            requestUploadExpirationPage: false,
            requestUploadPassport: false,
            requestUploadPassportTranslation: false,
            requestUploadPhoto: false
          }
        };
      default:
        return currentState;
    }
  };
  const [applicationFormState, dispatch] = useReducer(reducer, initialState);

  const passportFiles = [
    {
      fileName: 'passport.jpg',
      fileLabel: 'Passport Image',
      fieldName: 'passport',
      buttonText: 'Upload Revised Document',
      permittedContentTypes: FileUploadSpec.Passport.permittedContentTypes,
      permittedExtensions: FileUploadSpec.Passport.permittedExtensions,
      isEditable: initialState.requestedUpdate.requestUploadPassport,
      cropperProps: { aspect: 3 / 4 }
    },
    ...(initialState.requestedUpdate.requestUploadExpirationPage || !initialCaseDetails?.application?.passportDetails?.isPassportExpirationDateVisible
      ? [
          {
            fileName: 'passport-expiration-page',
            fileLabel: 'Passport Expiration Page',
            fieldName: 'passportExpiration',
            buttonText: 'Upload Revised Document',
            permittedContentTypes: FileUploadSpec.PassportExpirationPage.permittedContentTypes,
            permittedExtensions: FileUploadSpec.PassportExpirationPage.permittedExtensions,
            isEditable: initialState.requestedUpdate.requestUploadExpirationPage
          }
        ]
      : []),
    ...(initialState.requestedUpdate.requestUploadPassportTranslation || !initialCaseDetails?.application?.passportDetails?.isPassportInLatinCharacters
      ? [
          {
            fileName: 'passport-translation.pdf',
            fileLabel: 'English Translation of Passport',
            fieldName: 'passportTranslation',
            buttonText: 'Upload Revised Document',
            permittedContentTypes: FileUploadSpec.PassportTranslation.permittedContentTypes,
            permittedExtensions: FileUploadSpec.PassportTranslation.permittedExtensions,
            isEditable: initialState.requestedUpdate.requestUploadPassportTranslation
          }
        ]
      : [])
  ];

  const labelStyle = {
    fontSize: '12px'
  };

  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;

  const formatPassportDates = (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails) => {
    if (applicationFormState.isEditingPassportDetails) {
      values.passportIssuedAt = dayjs(values.passportIssuedAt).utc().format(APOLLO_DATE_FORMAT);
      values.passportExpiresAt = dayjs(values.passportExpiresAt).utc().format(APOLLO_DATE_FORMAT);
    }
    return values;
  };

  const handleSaveAndComplete = () => {
    passportDetailsForm
      .validateFields()
      .then(async (values: IdentityVerificationCaseV1CaseDetailsApplicationPassportDetails) => {
        formatPassportDates(values);
        props
          .onSaveAndComplete(values, {
            requestedChanges: {
              requestUpdatedPassportDetails: false,
              requestUploadExpirationPage: false,
              requestUploadPassport: false,
              requestUploadPassportTranslation: false,
              requestUploadPhoto: false
            },
            revisionSubmittedAt: new Date().toISOString()
          })
          .then(() => {
            dispatch({ type: 'save-complete' });
          });
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const handleSave = () => {
    passportDetailsForm
      .validateFields()
      .then((values) => {
        formatPassportDates(values);
        props.onSave(values).then(() => {
          dispatch({ type: 'save' });
        });
      })
      .catch((errorInfo) => {
        console.log('Form Validation failed:', errorInfo);
      });
  };

  const handleEditingPassportUpload = () => {
    dispatch({ type: 'edit-passport-uploads' });
  };

  const handleOnUploadSuccess = async (uploadedData: AzureBlobResponse, fieldName: string) => {
    await props.onUploadSuccess(uploadedData, fieldName);
  };

  return (
    <div style={{ paddingBottom: '80px' }}>
      <Descriptions labelStyle={labelStyle} layout="vertical" column={isMobile ? 1 : 3}>
        <Descriptions.Item label="Submission Date">{props.data.submittedAt ? formatDate(props.data.submittedAt) : 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Decision Date">
          {props.data.caseDetails?.applicationReview?.decision?.completedAt ? formatDate(props.data.caseDetails?.applicationReview?.decision?.completedAt) : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Case Id">{props.data.id}</Descriptions.Item>
        <Descriptions.Item label="Decision">{props.data.caseDetails?.applicationReview?.decision?.result ?? 'Pending'}</Descriptions.Item>
      </Descriptions>

      {!applicationFormState.isEditingPassportDetails && (
        <>
          <Descriptions
            extra={
              <Button
                type="primary"
                onClick={() => {
                  dispatch({
                    type: 'edit-passport-details'
                  });
                }}
                disabled={!initialRevisionRequest?.requestedChanges?.requestUpdatedPassportDetails}
              >
                Edit
              </Button>
            }
            labelStyle={labelStyle}
            layout="vertical"
            title="Passport Details"
          >
            <Descriptions.Item label="Country that Issued Passport">
              {initialCaseDetails?.application?.passportDetails?.passportCountry
                ? props.countryData.find((country) => country.countryCode === initialCaseDetails?.application?.passportDetails?.passportCountry)?.countryName
                : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
          <Descriptions labelStyle={labelStyle} layout="vertical" column={isMobile ? 1 : 3}>
            <Descriptions.Item label="Passport Issue Date">
              {initialCaseDetails?.application?.passportDetails?.passportIssuedAt ? formatDate(initialCaseDetails?.application?.passportDetails?.passportIssuedAt) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Passport Expiration Date">
              {initialCaseDetails?.application?.passportDetails?.passportExpiresAt ? formatDate(initialCaseDetails?.application?.passportDetails?.passportExpiresAt) : 'N/A'}
            </Descriptions.Item>
            <Descriptions.Item label="Passport Number">
              {initialCaseDetails?.application?.passportDetails?.passportNumber ? initialCaseDetails?.application?.passportDetails?.passportNumber : 'N/A'}
            </Descriptions.Item>
          </Descriptions>
        </>
      )}

      {applicationFormState.isEditingPassportDetails && (
        <CaseDetailsApplicationPassportDetailForm
          form={passportDetailsForm}
          caseDetails={initialCaseDetails as IdentityVerificationCaseV1CaseDetails}
          countryData={props.countryData}
        />
      )}

      {!applicationFormState.isEditingPassportUploads && (
        <Descriptions
          column={3}
          labelStyle={labelStyle}
          layout="vertical"
          extra={
            <Button
              type="primary"
              disabled={
                (!initialRevisionRequest?.requestedChanges?.requestUploadPassport &&
                  !initialRevisionRequest?.requestedChanges?.requestUploadExpirationPage &&
                  !initialRevisionRequest?.requestedChanges?.requestUploadPassportTranslation) ||
                applicationFormState.isEditingPassportUploads
              }
              onClick={() => {
                handleEditingPassportUpload();
              }}
            >
              {applicationFormState.isEditingPassportUploads ? 'Editing' : 'Edit'}
            </Button>
          }
        >
          {passportFiles.map((file) => {
            return (
              <Descriptions.Item label={file?.fileLabel ?? ''} key={file.fileName ?? ''} span={isMobile ? 3 : 1}>
                <IdCaseFileUploadButtonWithPreviewContainer
                  title={''}
                  buttonText={file.buttonText}
                  permittedContentTypes={file.permittedContentTypes}
                  permittedExtensions={file.permittedExtensions}
                  isPreviewOnly={!(applicationFormState.isEditingPassportUploads && file.isEditable)}
                  caseId={props.caseId}
                  fileName={file.fileName}
                  onUploadSuccess={async (response) => {
                    await handleOnUploadSuccess(response, file.fieldName);
                    applicationFormState.isEditingPassportUploads = false;
                  }}
                  isShowFileName={false}
                  cropperProps={file.cropperProps}
                />
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      )}

      {applicationFormState.isEditingPassportUploads && (
        <Descriptions column={3} labelStyle={labelStyle} layout="vertical" style={{ paddingTop: '30px' }}>
          {passportFiles.map((file) => {
            return (
              <Descriptions.Item label={file?.fileLabel ?? ''} key={file.fileName ?? ''} span={isMobile ? 3 : 1}>
                <IdCaseFileUploadButtonWithPreviewContainer
                  title={''}
                  buttonText={file.buttonText}
                  permittedContentTypes={file.permittedContentTypes}
                  permittedExtensions={file.permittedExtensions}
                  isPreviewOnly={!(applicationFormState.isEditingPassportUploads && file.isEditable)}
                  caseId={props.caseId}
                  fileName={file.fileName}
                  onUploadSuccess={(response) => handleOnUploadSuccess(response, file.fieldName)}
                  isShowFileName={false}
                  cropperProps={file.cropperProps}
                />
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      )}

      {!applicationFormState.isEditingPhotoUpload && (
        <Descriptions
          title={'Photograph'}
          layout="vertical"
          extra={
            <Button
              type="primary"
              disabled={!initialRevisionRequest?.requestedChanges?.requestUploadPhoto || applicationFormState.isEditingPhotoUpload}
              onClick={() => {
                dispatch({
                  type: 'edit-photo'
                });
              }}
            >
              {applicationFormState.isEditingPhotoUpload ? 'Editing' : 'Edit'}
            </Button>
          }
        >
          <Descriptions.Item label="Photograph">
            <IdCaseFileUploadButtonWithPreviewContainer
              title={''}
              buttonText={'Upload Photo'}
              permittedContentTypes={['image/jpeg']}
              permittedExtensions={['jpeg', 'jpg']}
              isPreviewOnly={!applicationFormState.isEditingPhotoUpload}
              onUploadSuccess={async (response) => {
                await handleOnUploadSuccess(response, 'photo');
                applicationFormState.isEditingPhotoUpload = false;
              }}
              caseId={props.caseId}
              fileName={'photo.jpg'}
              isShowFileName={false}
              cropperProps={{ aspect: 1 }}
            />
          </Descriptions.Item>
        </Descriptions>
      )}

      {applicationFormState.isEditingPhotoUpload && (
        <Descriptions title={'Photograph'} layout="vertical">
          <Descriptions.Item label="Photograph">
            <IdCaseFileUploadButtonWithPreviewContainer
              title={''}
              buttonText={'Upload Photo'}
              permittedContentTypes={FileUploadSpec.Photo.permittedContentTypes}
              permittedExtensions={FileUploadSpec.Photo.permittedExtensions}
              isPreviewOnly={!applicationFormState.isEditingPhotoUpload}
              onUploadSuccess={(response) => handleOnUploadSuccess(response, 'photo')}
              caseId={props.caseId}
              fileName={'photo.jpg'}
              isShowFileName={false}
              cropperProps={{ aspect: 1 }}
            />
          </Descriptions.Item>
        </Descriptions>
      )}

      <div
        style={{
          width: isMobile ? '100%' : '85%',
          display:
            applicationFormState.isEditingPassportDetails || applicationFormState.isEditingPassportUploads || applicationFormState.isEditingPhotoUpload ? 'flex' : 'none',
          justifyContent: 'space-between'
        }}
      >
        <Button
          onClick={() => {
            dispatch({ type: 'cancel' });
          }}
        >
          Cancel
        </Button>
        <div
          style={{
            display: 'flex',
            gap: '8px'
          }}
        >
          <Button onClick={handleSave}>Save</Button>
          <Button type="primary" onClick={handleSaveAndComplete}>
            Save and Complete
          </Button>
        </div>
      </div>
    </div>
  );
};
